import type { DashboardView } from '@afra/shared'
import { useQuery } from '@tanstack/react-query'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { api } from '../../lib/api'
import { faNum } from '../platform/labels'
import { PercentBars, TableToggle, TrendChart } from './charts'

const BASIS_FA: Record<string, string> = {
  legacy_quiz: 'خودارزیابی پیشین',
  inferred: 'استنتاجی',
  self_report: 'اظهار مؤسسه',
  explained: 'همراه با توضیح',
  document_verified: 'مستند',
  assessor_verified: 'تأیید ارزیاب',
}

/**
 * «داشبورد مؤسسه» and «پایش پیشرفت», as two tabs of one page.
 *
 * Every percentage counts what has not been asked yet as zero, the way the
 * certificate counts it, and says beside it how much is known — so «۳۰٪» reads
 * as «we know little» when that is the truth, not as «you are weak».
 */
export function DashboardPage() {
  const location = useLocation()
  const progress = location.pathname.endsWith('/progress')
  const data = useQuery({ queryKey: ['dashboard'], queryFn: api.dashboard })

  return (
    <div className="container stack">
      <div className="card dashboard-intro">
        <h1>{progress ? 'پایش پیشرفت' : 'داشبورد مؤسسه'}</h1>
        <p className="meta">
          {progress
            ? 'روند تغییر امتیاز بلوغ در طول زمان و وضعیت اجرای برنامه توسعه.'
            : 'وضعیت کلی بلوغ سازمانی، از آنچه تا امروز در گفت‌وگو، اسناد و خودارزیابی‌ها روشن شده.'}
        </p>
        <nav className="tabs" aria-label="بخش‌های داشبورد">
          <NavLink end to="/dashboard" className={({ isActive }) => `tab ${isActive ? 'is-active' : ''}`}>
            نمای کلی
          </NavLink>
          <NavLink to="/dashboard/progress" className={({ isActive }) => `tab ${isActive ? 'is-active' : ''}`}>
            پایش پیشرفت
          </NavLink>
        </nav>
      </div>

      {data.isLoading || !data.data ? (
        <p className="muted">در حال بارگذاری…</p>
      ) : progress ? (
        <Progress d={data.data} />
      ) : (
        <Overview d={data.data} />
      )}
    </div>
  )
}

function Overview({ d }: { d: DashboardView }) {
  const badgeFa = d.badge.awardedTier
    ? `نشان ${d.badge.awardedTier}`
    : d.badge.conditionalTier
      ? `${d.badge.conditionalTier}، مشروط`
      : 'هنوز نه'

  return (
    <>
      <div className="stats">
        <div className="stat">
          <div className="stat__label">امتیاز کل</div>
          <div className="stat__value num">{faNum(d.overall.percent)}٪</div>
          <div className="stat__basis">
            از <span className="num">{faNum(d.overall.known)}</span> مورد روشن‌شده، از{' '}
            <span className="num">{faNum(d.overall.total)}</span>
          </div>
        </div>
        <div className="stat">
          <div className="stat__label">وضعیت نشان افرا</div>
          <div className="stat__value">{badgeFa}</div>
          <div className="stat__basis">
            {d.badge.authoritative ? 'محاسبه رسمی' : 'محاسبه غیررسمی'} · <Link to="/certificate">جزئیات</Link>
          </div>
        </div>
        <div className="stat">
          <div className="stat__label">کارهای باز</div>
          <div className="stat__value num">{faNum(d.tasks.open)}</div>
          <div className="stat__basis">
            <span className="num">{faNum(d.tasks.overdue)}</span> مورد مهلتش گذشته
          </div>
        </div>
        <div className="stat">
          <div className="stat__label">بازبینی ارزیاب</div>
          <div className="stat__value">{reviewFa(d.review?.status)}</div>
          <div className="stat__basis">
            <Link to="/certificate">ارسال یا پیگیری</Link>
          </div>
        </div>
      </div>

      <div className="grid-2 grid-2--cards">
        <div className="card">
          <h2>امتیاز هر بخش</h2>
          <PercentBars
            floor={50}
            floorFa="کف لازم برای هر بخش در نشان افرا: ۵۰٪"
            data={d.dimensions.map((x) => ({
              key: x.code,
              labelFa: x.titleFa,
              percent: x.percent,
              noteFa:
                `${faNum(x.known)} از ${faNum(x.total)} روشن شده` +
                (x.claimsOnly > 0 ? ` · ${faNum(x.claimsOnly)} مورد فقط بر پایه گفته` : ''),
            }))}
          />
          <TableToggle labelFa="دیدن به شکل جدول">
            <table className="table">
              <thead>
                <tr>
                  <th>بخش</th>
                  <th>امتیاز</th>
                  <th>روشن‌شده</th>
                  <th>فقط بر پایه گفته</th>
                </tr>
              </thead>
              <tbody>
                {d.dimensions.map((x) => (
                  <tr key={x.code}>
                    <td>{x.titleFa}</td>
                    <td className="num">{faNum(x.percent)}٪</td>
                    <td className="num">
                      {faNum(x.known)}/{faNum(x.total)}
                    </td>
                    <td className="num">{faNum(x.claimsOnly)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableToggle>
        </div>

        <div className="card">
          <h2>سطح‌های توسعه</h2>
          <PercentBars
            data={d.levels.map((l) => ({
              key: String(l.levelNo),
              labelFa: `سطح ${faNum(l.levelNo)} · ${l.titleFa}`,
              percent: l.percent,
              noteFa: `نشان ${l.tier} · ${faNum(l.known)} از ${faNum(l.total)} روشن شده`,
            }))}
            floor={70}
            floorFa="آستانه نشان کامل: ۷۰٪ امتیاز آن سطح"
          />
        </div>
      </div>

      <div className="grid-2 grid-2--cards">
        <div className="card">
          <h2>شکاف‌های اولویت‌دار</h2>
          {d.gaps.length === 0 ? (
            <p className="meta">هنوز موردی با امتیاز پایین روشن نشده.</p>
          ) : (
            <div className="table-scroll">
              <table className="table table--compact">
                <thead>
                  <tr>
                    <th>بخش</th>
                    <th>موضوع</th>
                    <th>امتیاز</th>
                  </tr>
                </thead>
                <tbody>
                  {d.gaps.map((g) => (
                    <tr key={`${g.code}-${g.levelNo}`}>
                      <td>{g.dimensionTitleFa}</td>
                      <td>
                        {g.textFa.length > 120 ? `${g.textFa.slice(0, 120)}…` : g.textFa}
                        {g.isKey && <span className="badge badge--bad">کلیدی</span>}
                      </td>
                      <td>
                        <span className="num">{faNum(g.score ?? 0)}</span> از <span className="num">۳</span>
                        <div className="meta">{BASIS_FA[g.basis ?? ''] ?? ''}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Link to="/roadmap" className="btn btn--ghost btn--sm" style={{ marginTop: 'var(--space-3)' }}>
            دیدن مسیر توسعه
          </Link>
        </div>

        <div className="card">
          <h2>یادآوری‌ها</h2>
          {d.reminders.length === 0 ? (
            <p className="meta">یادآوری‌ای نیست.</p>
          ) : (
            d.reminders.map((r) => (
              <div key={r.textFa} className="note">
                <span className={`badge ${r.tone === 'bad' ? 'badge--bad' : r.tone === 'warn' ? 'badge--warn' : 'badge--stored'}`}>
                  {r.tone === 'bad' ? 'گذشته' : r.tone === 'warn' ? 'نزدیک' : 'خبر'}
                </span>
                <span>{r.textFa}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  )
}

function Progress({ d }: { d: DashboardView }) {
  const done = d.tasks.total === 0 ? 0 : Math.round((d.tasks.done / d.tasks.total) * 100)
  return (
    <>
      <div className="card">
        <h2>روند امتیاز کل در طول زمان</h2>
        {d.trend.length < 2 ? (
          <p className="meta">برای کشیدن روند دست‌کم دو روز تغییر لازم است. با ادامه گفت‌وگو و فرستادن اسناد پر می‌شود.</p>
        ) : (
          <>
            <TrendChart
              labelFa="درصد امتیاز کل مؤسسه در طول زمان"
              points={d.trend.map((p) => ({ on: p.on, value: p.percent, noteFa: `${faNum(p.known)} مورد روشن‌شده` }))}
            />
            <TableToggle labelFa="دیدن به شکل جدول">
              <table className="table table--compact">
                <thead>
                  <tr>
                    <th>روز</th>
                    <th>امتیاز کل</th>
                    <th>روشن‌شده</th>
                  </tr>
                </thead>
                <tbody>
                  {d.trend.map((p) => (
                    <tr key={p.on}>
                      <td>{new Intl.DateTimeFormat('fa-IR-u-ca-persian', { dateStyle: 'medium' }).format(new Date(`${p.on}T12:00:00Z`))}</td>
                      <td className="num">{faNum(p.percent)}٪</td>
                      <td className="num">{faNum(p.known)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableToggle>
          </>
        )}
        <p className="meta">
          امتیاز کل یعنی جمع امتیازها تقسیم بر بیشترین امتیاز ممکن؛ موردی که هنوز روشن نشده صفر حساب می‌شود، پس بخشی از
          بالا رفتن خط فقط از روشن شدن موارد تازه است.
        </p>
      </div>

      <div className="card">
        <h2>وضعیت کلی کارها</h2>
        <div className="stats">
          <div className="stat">
            <div className="stat__label">کارهای باز</div>
            <div className="stat__value num">{faNum(d.tasks.open)}</div>
          </div>
          <div className="stat">
            <div className="stat__label">مهلتشان گذشته</div>
            <div className="stat__value num">{faNum(d.tasks.overdue)}</div>
          </div>
          <div className="stat">
            <div className="stat__label">انجام‌شده</div>
            <div className="stat__value num">{faNum(done)}٪</div>
            <div className="stat__basis">
              <span className="num">{faNum(d.tasks.done)}</span> از <span className="num">{faNum(d.tasks.total)}</span>
            </div>
          </div>
        </div>
        <Link to="/planning" className="btn btn--ghost btn--sm" style={{ marginTop: 'var(--space-3)' }}>
          رفتن به برنامه‌ریزی
        </Link>
      </div>
    </>
  )
}

function reviewFa(status: string | undefined): string {
  switch (status) {
    case 'submitted':
      return 'در صف'
    case 'in_review':
      return 'در حال بازبینی'
    case 'finalized':
      return 'نهایی شده'
    case 'returned':
      return 'برگشت داده شده'
    default:
      return 'فرستاده نشده'
  }
}
